import { useMemo, useState } from 'react';
import { Heart, Plus, Flame, Check, X, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useWishlist } from '@/data/hooks/useWishlist';
import { useAuth } from '@/contexts/AuthContext';
import { WishlistSheet } from './WishlistSheet';
import type { WishlistItem } from '@/types';
import { timeAgo } from '@/data/mockData';
import { toast } from '@/hooks/use-toast';

function vehicleSummary(w: WishlistItem) {
  const parts: string[] = [];
  if (w.year_min || w.year_max) {
    if (w.year_min && w.year_max) parts.push(`${w.year_min}–${w.year_max}`);
    else if (w.year_min) parts.push(`${w.year_min}+`);
    else if (w.year_max) parts.push(`up to ${w.year_max}`);
  }
  if (w.color) parts.push(w.color);
  parts.push(w.make);
  if (w.model) parts.push(w.model);
  return parts.join(' ');
}

function urgencyBadge(u: WishlistItem['urgency']) {
  if (u === 'hot') return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary/15 text-primary"><Flame className="h-3 w-3" />HOT</span>;
  if (u === 'cold') return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-muted text-muted-foreground">COLD</span>;
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-500/15 text-yellow-400">WARM</span>;
}

export function MyWishlistCard() {
  const { mode, userId } = useAuth();
  const { items, loading, update } = useWishlist();
  const [editing, setEditing] = useState<WishlistItem | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const repId = mode === 'demo' ? 'rep-alex' : userId;

  const handleStatusChange = async (
    e: React.MouseEvent,
    item: WishlistItem,
    status: 'fulfilled' | 'dropped',
  ) => {
    e.stopPropagation();
    if (mode !== 'real') {
      toast({
        title: 'Demo mode',
        description: 'Sign in to update wishlists.',
      });
      return;
    }
    setBusyId(item.id);
    try {
      await update(item.id, { status });
      toast({
        title: status === 'fulfilled' ? 'Marked as found' : 'Marked as dropped',
        description: `${item.customer_name} — ${item.make}${item.model ? ' ' + item.model : ''}`,
      });
    } catch (err: any) {
      toast({
        title: 'Could not update',
        description: err?.message ?? 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };
  const mine = useMemo(() => {
    const order = { hot: 0, warm: 1, cold: 2 };
    return items
      .filter(w => w.rep_id === repId && w.status === 'active')
      .sort((a, b) => order[a.urgency] - order[b.urgency] || (b.created_at.localeCompare(a.created_at)));
  }, [items, repId]);

  return (
    <>
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" /> My Wishlist
          </CardTitle>
          <Button size="sm" variant="ghost" className="gap-1" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground py-4">Loading…</p>
          ) : mine.length === 0 ? (
            <div className="text-center py-6 space-y-2">
              <p className="text-sm text-muted-foreground">
                Capture what your customers are looking for so nothing slips when a match comes in.
              </p>
              <Button size="sm" variant="outline" className="gap-1" onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" /> Add first wishlist
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {mine.map(w => (
                <div
                  key={w.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setEditing(w)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setEditing(w); } }}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors text-left cursor-pointer"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{w.customer_name}</p>
                      {urgencyBadge(w.urgency)}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">Wants: {vehicleSummary(w)}</p>
                    {w.notes && <p className="text-xs text-muted-foreground/80 truncate mt-0.5">{w.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <div className="text-right">
                      {w.max_price && <p className="text-xs font-medium">≤ ${(w.max_price / 1000).toFixed(0)}K</p>}
                      <p className="text-xs text-muted-foreground">{timeAgo(w.created_at)}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        aria-label="Mark as found"
                        title="Mark as found"
                        disabled={busyId === w.id}
                        onClick={(e) => handleStatusChange(e, w, 'fulfilled')}
                        className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-green-500/15 text-green-400 hover:bg-green-500/25 transition-colors disabled:opacity-50"
                      >
                        {busyId === w.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        type="button"
                        aria-label="Mark as dropped"
                        title="Mark as dropped"
                        disabled={busyId === w.id}
                        onClick={(e) => handleStatusChange(e, w, 'dropped')}
                        className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-muted text-muted-foreground hover:bg-muted/70 transition-colors disabled:opacity-50"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <WishlistSheet open={addOpen} onOpenChange={setAddOpen} />
      {editing && (
        <WishlistSheet
          open={!!editing}
          onOpenChange={(o) => !o && setEditing(null)}
          item={editing}
        />
      )}
    </>
  );
}